import {
  ColumnDefinition,
  OuiButtonComponent,
  OuiServerSideDatasource as OuiDatasource,
  OuiDataTableComponent,
  OuiIconButtonComponent,
  OuiPaginatorComponent,
  OuiRowComponent,
  OuiTextComponent,
  OuiTooltipComponent,
  NotificationService,
} from '@aiz-gl/omni-ui';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Observable } from 'rxjs';
import { DownloadButtonComponent } from '../../../components/download-button/download-button.component';
import { InputComponent } from '../../../components/input/input.component';
import { PageComponent } from '../../../components/page/page';
import { User } from '../../../models/user.models';
import { UserService } from '../../../services/user/user.service';
import { BasePageComponent } from '../../base/base.component';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    OuiDataTableComponent,
    OuiPaginatorComponent,
    OuiRowComponent,
    OuiTextComponent,
    OuiIconButtonComponent,
    OuiTooltipComponent,
    OuiButtonComponent,
    DownloadButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    PageComponent,
    RouterLink,
  ],
  templateUrl: './users.component.html',
})
export class UsersPage extends BasePageComponent {
  private userService = inject(UserService);
  private notifications = inject(NotificationService);

  users = new OuiDatasource<User>(this.userService.userSearchDataLoader, 15);
  showDialog = signal<boolean>(false);
  searchTerm = signal<string>('');
  isSyncingUsers = signal<boolean>(false);

  searchForm = new FormGroup({
    search: new FormControl(''),
  });

  usersColumns: ColumnDefinition<User>[] = [
    {
      columnDef: 'firstName',
      header: 'First Name',
    },
    {
      columnDef: 'lastName',
      header: 'Last Name',
    },
    {
      columnDef: 'email',
      header: 'Email',
    },
    {
      columnDef: 'isAdmin',
      header: 'Is Admin',
    },
    {
      columnDef: 'isLincUser',
      header: 'Is LINC User',
    },
  ];
  constructor() {
    super();

    // Keep initial table sort state aligned with initial API payload sorting.
    this.users.sortRecords({ column: 'firstName', direction: 'asc' });
  }

  searchUsers() {
    const value = (this.searchForm.value.search || '').trim();
    if (!value) {
      return;
    }

    this.searchTerm.set(value);
    this.users.setFilter({ search: value });
  }

  canSearch(): boolean {
    return (this.searchForm.controls.search.value || '').trim().length > 0;
  }

  resetSearch() {
    this.searchForm.reset({ search: '' });
    this.searchTerm.set('');
    this.users.clearFilter();
  }

  downloadUsers(): Observable<HttpResponse<Blob>> {
    const search = this.searchTerm().trim();
    const hasSearch = search.length > 0;
    const payload = {
      pageNumber: 0,
      pageSize: 0,
      sortBy: 'firstName',
      sortDirection: 'asc',
      ...(hasSearch
        ? {
            firstName: search,
            lastName: search,
            email: search,
          }
        : {}),
    };

    return this.userService.downloadUsers(payload);
  }

  syncUsers() {
    if (this.isSyncingUsers()) {
      return;
    }

    this.isSyncingUsers.set(true);
    this.userService.syncUsers().subscribe({
      next: () => {
        const currentFilter = this.users.getPageRequest().filter || {};
        this.users.setFilter(currentFilter);
        this.notifications.showSnackbar({
          message: 'Sync completed successfully.',
          variant: 'success',
          symbolName: 'check_circle',
        });
      },
      complete: () => {
        this.isSyncingUsers.set(false);
      },
      error: () => {
        this.isSyncingUsers.set(false);
      },
    });
  }

  getUsersDownloadFilename(): string {
    const now = new Date();
    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;

    return `ATB_Users_List_${timestamp}.xlsx`;
  }

  handleRowClick(user: User) {
    this.router.navigate([`/user-management/update-user/${user.email}`], {
      state: user,
    });
  }
}
